import { z } from "zod";

import { MAP_THEMES, normalizeMapTheme } from "../../render/mapThemes";
import {
  TILE_KINDS,
  type BuildingTileKind,
  type UnitKind,
  UNIT_KINDS,
  VICTORY_CONDITIONS,
} from "./primitives";

const UnitKindSchema = z.enum(UNIT_KINDS);
const MapThemeSchema = z.preprocess(
  (theme) => normalizeMapTheme(theme),
  z.enum(MAP_THEMES).optional(),
);

const PlayerIdSchema = z.union([z.literal("p1"), z.literal("p2")]);
const TileKindSchema = z.enum(TILE_KINDS);
const TileOwnerSchema = z.union([PlayerIdSchema, z.literal("neutral")]);
const FacingDirSchema = z.union([
  z.literal("up"),
  z.literal("down"),
  z.literal("left"),
  z.literal("right"),
]);

const CoordSchema = z.object({
  x: z.number(),
  y: z.number(),
});

const TileSchema = z.object({
  kind: TileKindSchema,
  owner: TileOwnerSchema.optional(),
  capturePoints: z.number().optional(),
});

const UnitCargoSchema = z.object({
  kind: UnitKindSchema,
  hp: z.number(),
  kills: z.number().optional(),
  captures: z.number().optional(),
});

const UnitSchema = z.object({
  id: z.string(),
  owner: PlayerIdSchema,
  kind: UnitKindSchema,
  pos: CoordSchema,
  hp: z.number(),
  hasMoved: z.boolean(),
  hasAttacked: z.boolean(),
  facing: FacingDirSchema,
  cargo: UnitCargoSchema.optional(),
  unloadReady: z.boolean().optional(),
  kills: z.number().optional(),
  captures: z.number().optional(),
});

const VictoryConditionSchema = z.enum(VICTORY_CONDITIONS);

const GameStateSchema = z.object({
  width: z.number(),
  height: z.number(),
  theme: MapThemeSchema.optional(),
  victoryConditions: z.array(VictoryConditionSchema).optional(),
  captureCityTarget: z.number().optional(),
  tiles: z.array(TileSchema),
  units: z.array(UnitSchema),
  currentPlayer: PlayerIdSchema,
  currentDay: z.number(),
  status: z.union([z.literal("in_progress"), z.literal("victory"), z.literal("defeat")]),
  funds: z.record(PlayerIdSchema, z.number()),
  nextUnitId: z.number(),
  initialUnitCount: z.number().optional(),
});

export type GameState = z.infer<typeof GameStateSchema>;

const PersistedUnitSchema = z.object({
  id: z.string(),
  owner: PlayerIdSchema,
  kind: UnitKindSchema,
  pos: CoordSchema,
  hp: z.number(),
  facing: FacingDirSchema,
  cargo: UnitCargoSchema.optional(),
  kills: z.number().optional(),
  captures: z.number().optional(),
});

export const PersistedGameStateSchema = GameStateSchema.omit({ units: true }).extend({
  units: z.array(PersistedUnitSchema),
});

export type PersistedGameState = z.infer<typeof PersistedGameStateSchema>;

export function hydratePersistedGameState(s: PersistedGameState): GameState {
  return {
    ...s,
    units: s.units.map((u) => ({
      ...u,
      hasMoved: false,
      hasAttacked: false,
      unloadReady: false,
    })),
  };
}

function persistedTile(tile: z.infer<typeof TileSchema>): z.infer<typeof TileSchema> {
  return {
    kind: tile.kind,
    ...(tile.owner !== undefined ? { owner: tile.owner } : {}),
    ...(tile.capturePoints !== undefined ? { capturePoints: tile.capturePoints } : {}),
  };
}

function persistedOptionalCount(value: number | undefined): number | undefined {
  return value !== undefined && value > 0 ? value : undefined;
}

function persistedCargo(
  cargo: NonNullable<z.infer<typeof UnitSchema>["cargo"]>,
): z.infer<typeof UnitCargoSchema> {
  const kills = persistedOptionalCount(cargo.kills);
  const captures = persistedOptionalCount(cargo.captures);
  return {
    kind: cargo.kind,
    hp: cargo.hp,
    ...(kills !== undefined ? { kills } : {}),
    ...(captures !== undefined ? { captures } : {}),
  };
}

export function toPersistedGameState(s: GameState): PersistedGameState {
  return {
    ...s,
    tiles: s.tiles.map(persistedTile),
    units: s.units.map(({ id, owner, kind, pos, hp, facing, cargo, kills, captures }) => {
      const persistedKills = persistedOptionalCount(kills);
      const persistedCaptures = persistedOptionalCount(captures);
      return {
        id,
        owner,
        kind,
        pos,
        hp,
        facing,
        ...(cargo !== undefined ? { cargo: persistedCargo(cargo) } : {}),
        ...(persistedKills !== undefined ? { kills: persistedKills } : {}),
        ...(persistedCaptures !== undefined ? { captures: persistedCaptures } : {}),
      };
    }),
  };
}

export type { BuildingTileKind, UnitKind };
